import { memo, type MouseEvent, type ReactNode } from 'react'
import { MessageSquareText, Mic } from 'lucide-react'
import { Cell, UnreadDot, unreadDotCutoutStyle } from '@highlight/ui'
import { MeetingIcon } from '@renderer/chat'
import { useIsTurnLive } from '@renderer/chat/store/turn-status'
import { StreamingChatIndicator } from '@renderer/components/streaming-chat-indicator'
import { CellActionsMenu, CellContextMenu } from '../cell-actions'
import { SidepanelCellRow } from '../cells/row'
import {
  formatTimeAgo,
  getActionIcon,
  getActivityTitle,
  isKnownMeetingAppName,
} from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { isActivityUnread } from '@renderer/overlay/Sidepanel/utils/activity-unread'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'
import type { ViewOpenTarget } from '@renderer/overlay/Sidepanel/view-atoms'

/**
 * The 20px leading slot: actions carry a real third-party logo in the cell icon's
 * well; meetings show their source app bare; everything else is a bare
 * glyph. A chat with an unread response also carries the dot cells use. Exported
 * so the History view can render it *inside* its selection checkbox (which
 * cross-fades icon ↔ checkbox on row hover).
 */
export function HistoryRowIcon({ item }: { item: RecentActivity }) {
  const isStreaming = useIsTurnLive(item.type === 'chat' ? item.id : undefined)

  if (isStreaming) {
    return (
      <div className="flex size-5 shrink-0 items-center justify-center">
        <StreamingChatIndicator label={`${getActivityTitle(item)} is working`} size={16} />
      </div>
    )
  }

  if (item.type === 'completed-action') {
    return <Cell.Icon>{getActionIcon(item)}</Cell.Icon>
  }
  if (item.type === 'audio-note' && isKnownMeetingAppName(item.meetingSource)) {
    return (
      <div className="flex size-5 shrink-0 items-center justify-center">
        <MeetingIcon appName={item.meetingSource} size={16} />
      </div>
    )
  }
  const glyphClass = 'text-foreground-secondary flex size-5 shrink-0 items-center justify-center [&_svg]:size-4'
  const glyph = item.type === 'chat' ? <MessageSquareText /> : <Mic />

  // The dot is a sibling of the masked glyph, not a child. A child would be masked with it.
  if (isActivityUnread(item)) {
    return (
      <div className="relative shrink-0">
        <div className={glyphClass} style={unreadDotCutoutStyle}>
          {glyph}
        </div>
        <UnreadDot />
      </div>
    )
  }
  return <div className={glyphClass}>{glyph}</div>
}

export interface FlatActivityRowProps {
  ariaLabel: string
  onOpen: (event: MouseEvent<HTMLDivElement>) => void
  /**
   * The same open, forced into a window. Given, the row offers it on right-click
   * — a flat row has no hover cluster to hang it off.
   */
  onOpenInWindow?: () => void
  /** The 20px leading slot — an icon, or the History view's selection checkbox. */
  leading: ReactNode
  title: string
  /** A line under the title, such as the passage a search matched. */
  subtitle?: ReactNode
  /** Rendered right-aligned as an abbreviated "time ago". */
  timestamp: Date | string
}

/**
 * The shared flat activity row per Figma (`7534:35175` in the History view,
 * `7345:12812` in Home's Recents): a 20px icon slot, the title, and a
 * right-aligned abbreviated timestamp — no card chrome. `HistoryRow` and the
 * Upcoming view's `PastEventRow` are both this shell over different sources.
 */
export function FlatActivityRow({
  ariaLabel,
  onOpen,
  onOpenInWindow,
  leading,
  title,
  subtitle,
  timestamp,
}: FlatActivityRowProps) {
  const row = (
    <SidepanelCellRow
      icon={leading}
      title={title}
      subtitle={subtitle}
      trailing={
        <span className="text-foreground-secondary type-ui-xs shrink-0 whitespace-nowrap pt-[2px] font-normal">
          {formatTimeAgo(timestamp)}
        </span>
      }
      ariaLabel={ariaLabel}
      className="px-3 py-2"
      onClick={onOpen}
    />
  )

  if (!onOpenInWindow) return row

  return <CellContextMenu items={<CellActionsMenu onPopout={onOpenInWindow} />}>{row}</CellContextMenu>
}

interface HistoryRowProps {
  item: RecentActivity
  onOpen: (item: RecentActivity, open: ViewOpenTarget) => void
  /**
   * Replaces the plain icon in the leading slot — the History view passes its
   * selection checkbox here. Omit for a non-selectable row (Home Recents), which
   * also keeps the selection context (and its bulk-delete bar) out of Home.
   */
  leading?: ReactNode
  /** A line under the title, such as the passage a search matched. */
  subtitle?: ReactNode
}

/**
 * Memoized so it only re-renders when its own item changes, not when the list
 * container re-renders. Callers passing `leading` must memoize that element for
 * the same reason.
 */
export const HistoryRow = memo(function HistoryRow({ item, onOpen, leading, subtitle }: HistoryRowProps) {
  return (
    <FlatActivityRow
      ariaLabel={getActivityTitle(item)}
      onOpen={(pointerEvent) => onOpen(item, { pointerEvent })}
      onOpenInWindow={() => onOpen(item, { popout: true })}
      leading={leading ?? <HistoryRowIcon item={item} />}
      title={getActivityTitle(item)}
      subtitle={subtitle}
      timestamp={item.unifiedUpdatedAt}
    />
  )
})
